/**
 * 7-card hand evaluator.
 *
 * Card = int 0..51. rank = c >> 2 (0='2' … 12='A'), suit = c & 3.
 * Returns an integer score; higher is better. Ported from the prototype engine,
 * where it was checked against exhaustive enumeration before being trusted.
 */

export const RANKS = "23456789TJQKA";
export const SUITS = "cdhs";
export const rankOf = (c: number) => c >> 2;
export const suitOf = (c: number) => c & 3;
export const cardStr = (c: number) => RANKS[rankOf(c)]! + SUITS[suitOf(c)]!;

const CAT = { HIGH: 0, PAIR: 1, TWO: 2, TRIPS: 3, STR: 4, FLUSH: 5, BOAT: 6, QUADS: 7, SF: 8 };

function straightHigh(mask: number): number {
  for (let hi = 12; hi >= 4; hi--) {
    let ok = true;
    for (let k = 0; k < 5; k++) if (!(mask & (1 << (hi - k)))) { ok = false; break; }
    if (ok) return hi;
  }
  // The wheel: A2345, where the ace plays low.
  if ((mask & (1 << 12)) && (mask & 1) && (mask & 2) && (mask & 4) && (mask & 8)) return 3;
  return -1;
}

function pack(cat: number, ks: number[]): number {
  let v = cat;
  for (let i = 0; i < 5; i++) v = v * 16 + (ks[i] !== undefined ? ks[i]! + 1 : 0);
  return v;
}

export function evaluate7(cards: number[] | Int8Array | Uint8Array): number {
  const rc = new Int8Array(13);
  const sc = new Int8Array(4);
  const bySuit: number[][] = [[], [], [], []];
  let mask = 0;
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i]!;
    const r = c >> 2;
    const s = c & 3;
    rc[r]!++;
    sc[s]!++;
    bySuit[s]!.push(r);
    mask |= 1 << r;
  }

  for (let s = 0; s < 4; s++) {
    if (sc[s]! >= 5) {
      let fm = 0;
      for (const r of bySuit[s]!) fm |= 1 << r;
      const sfh = straightHigh(fm);
      if (sfh >= 0) return pack(CAT.SF, [sfh, 0, 0, 0, 0]);
      const rs = bySuit[s]!.slice().sort((a, b) => b - a).slice(0, 5);
      return pack(CAT.FLUSH, rs);
    }
  }

  const sh = straightHigh(mask);
  const quads: number[] = [], trips: number[] = [], pairs: number[] = [], singles: number[] = [];
  for (let r = 12; r >= 0; r--) {
    if (rc[r] === 4) quads.push(r);
    else if (rc[r] === 3) trips.push(r);
    else if (rc[r] === 2) pairs.push(r);
    else if (rc[r] === 1) singles.push(r);
  }

  if (quads.length) {
    let kicker = 0;
    for (let r = 12; r >= 0; r--) if (r !== quads[0] && rc[r]! > 0) { kicker = r; break; }
    return pack(CAT.QUADS, [quads[0]!, kicker, 0, 0, 0]);
  }
  if (trips.length >= 2) return pack(CAT.BOAT, [trips[0]!, trips[1]!, 0, 0, 0]);
  if (trips.length && pairs.length) return pack(CAT.BOAT, [trips[0]!, pairs[0]!, 0, 0, 0]);
  if (sh >= 0) return pack(CAT.STR, [sh, 0, 0, 0, 0]);
  if (trips.length) {
    const k = [trips[0]!];
    for (const r of singles) if (k.length < 3) k.push(r);
    return pack(CAT.TRIPS, [k[0]!, k[1]!, k[2]!, 0, 0]);
  }
  if (pairs.length >= 2) {
    let kicker = 0;
    for (let r = 12; r >= 0; r--) if (r !== pairs[0] && r !== pairs[1] && rc[r]! > 0) { kicker = r; break; }
    return pack(CAT.TWO, [pairs[0]!, pairs[1]!, kicker, 0, 0]);
  }
  if (pairs.length === 1) {
    const k = [pairs[0]!];
    for (const r of singles) if (k.length < 4) k.push(r);
    return pack(CAT.PAIR, [k[0]!, k[1]!, k[2]!, k[3]!, 0]);
  }
  return pack(CAT.HIGH, singles.slice(0, 5));
}

/* ------------------------------------------------------------- hand codes --- */

/** The 169 canonical starting hands, e.g. "AA", "AKs", "AKo". */
export const HANDS: string[] = (() => {
  const out: string[] = [];
  for (let hi = 12; hi >= 0; hi--) {
    for (let lo = 12; lo >= 0; lo--) {
      if (hi === lo) { if (hi >= lo) out.push(RANKS[hi]! + RANKS[lo]!); }
      else if (hi > lo) out.push(RANKS[hi]! + RANKS[lo]! + "s");
      else out.push(RANKS[lo]! + RANKS[hi]! + "o");
    }
  }
  return [...new Set(out)];
})();

/** Every specific two-card combo for a canonical hand code. */
export function combosOf(code: string): [number, number][] {
  const r1 = RANKS.indexOf(code[0]!);
  const r2 = RANKS.indexOf(code[1]!);
  const kind = code[2];
  const out: [number, number][] = [];
  if (r1 === r2) {
    for (let a = 0; a < 4; a++) for (let b = a + 1; b < 4; b++) out.push([r1 * 4 + a, r1 * 4 + b]);
  } else if (kind === "s") {
    for (let s = 0; s < 4; s++) out.push([r1 * 4 + s, r2 * 4 + s]);
  } else {
    for (let a = 0; a < 4; a++) for (let b = 0; b < 4; b++) if (a !== b) out.push([r1 * 4 + a, r2 * 4 + b]);
  }
  return out;
}

/** Combos per hand: 6 for a pair, 4 suited, 12 offsuit. */
export function comboCount(code: string): number {
  return code.length === 2 ? 6 : code[2] === "s" ? 4 : 12;
}
